<!-- eslint-disable -->
<template>
  <div id="whatsapp-settings" class="wrapper mb-1">
    <page-heading title="WhatsApp Settings" border>
      <template #content>
        <div class="whatsapp-filters">
          <Select
            v-if="activeTab === 'messages'"
            styles="toolbar-select"
            placeholder="Today"
            :input="filterByDate"
            :options="dateFilterOptions"
          />
          <Select
            v-if="activeTab === 'messages'"
            styles="toolbar-select"
            placeholder="Status"
            :input="filterByStatus"
            :options="statusFilterOptions"
          />
          <Input
            v-if="activeTab === 'messages'"
            :value="searchQuery"
            @input="searchQuery = $event"
            placeholder="Search patient or phone"
            class="toolbar-search"
          />
          <Button
            v-if="activeTab === 'messages'"
            :onClick="refreshMessages"
            className="button--primary toolbar-action"
            title="Refresh"
          />
        </div>
      </template>
    </page-heading>

    <!-- Tabs Navigation -->
    <div class="tabs-container">
      <div class="tabs-nav">
        <div
          class="tab-item"
          :class="{ active: activeTab === 'messages' }"
          @click="setActiveTab('messages')"
        >
          <span class="tab-icon mdi mdi-message-text-outline"></span>
          Messages
        </div>
        <div
          class="tab-item"
          :class="{ active: activeTab === 'failed' }"
          @click="setActiveTab('failed')"
        >
          <span class="tab-icon mdi mdi-alert-circle-outline"></span>
          Failed
          <span class="badge">{{ failedMessagesCount }}</span>
        </div>

        <div
          class="tab-item"
          :class="{ active: activeTab === 'billing' }"
          @click="setActiveTab('billing')"
        >
          <span class="tab-icon mdi mdi-credit-card-outline"></span>
          Billing
        </div>
        <div
          class="tab-item"
          :class="{ active: activeTab === 'message-settings' }"
          @click="setActiveTab('message-settings')"
        >
          <span class="tab-icon mdi mdi-cog-outline"></span>
          Delivery Rules
        </div>
      </div>
    </div>

    <!-- Tab Contents -->
    <div class="tab-content">
      <!-- Messages Tab -->
      <div v-if="activeTab === 'messages'" class="messages-tab">
        <!-- Statistics Cards -->
        <div class="stats-grid">
          <div class="stat-card sent">
            <div class="stat-icon mdi mdi-send-check-outline"></div>
            <div class="stat-content">
              <h3>Sent</h3>
              <div class="stat-number">{{ statistics.totalSentToday }}</div>
            </div>
          </div>

          <div class="stat-card queued">
            <div class="stat-icon mdi mdi-clock-outline"></div>
            <div class="stat-content">
              <h3>Queued</h3>
              <div class="stat-number">{{ statistics.inQueue }}</div>
            </div>
          </div>

          <div class="stat-card processing">
            <div class="stat-icon mdi mdi-progress-clock"></div>
            <div class="stat-content">
              <h3>Processing</h3>
              <div class="stat-number">{{ statistics.processing }}</div>
            </div>
          </div>

          <div class="stat-card failed">
            <div class="stat-icon mdi mdi-alert-outline"></div>
            <div class="stat-content">
              <h3>Failed</h3>
              <div class="stat-number">{{ statistics.failed }}</div>
            </div>
          </div>
        </div>

        <!-- Messages Table -->
        <div class="table-container">
          <data-table
            :cols="messageColumns"
            :rows="filteredMessages"
            height="calc(100vh - 25rem)"
            :loading="loading"
            :pagination="true"
            :current-page="pagination.page"
            :total-pages="pagination.totalPages"
            :result-count="pagination.total"
            :items-per-page="pagination.limit"
            :on-page-changed="handlePageChange"
          >
            <template slot="status" slot-scope="{ status }">
              <span :class="['status-badge', status.toLowerCase()]">
                {{ status }}
              </span>
            </template>
            <template slot="action" slot-scope="{ actions }">
              <dropdown-menu
                :rowData="actions"
                :handleItemClick="handleMessageAction"
                :items="['View Details', 'Retry', 'Delete']"
              />
            </template>
          </data-table>
        </div>
      </div>

      <!-- Failed Messages Tab -->
      <div v-if="activeTab === 'failed'" class="failed-tab">
        <div class="failed-header">
          <h3>Failed deliveries</h3>
          <Button
            :onClick="retryAllFailed"
            className="button--primary"
            title="Retry failed"
          />
        </div>

        <data-table
          :cols="failedMessageColumns"
          :rows="failedMessagesComputed"
          height="calc(100vh - 20rem)"
          :loading="loading"
        >
          <template slot="error" slot-scope="{ failureReason }">
            <span class="error-message">{{
              failureReason || "Unknown error"
            }}</span>
          </template>
          <template slot="action" slot-scope="{ actions }">
            <dropdown-menu
              :rowData="actions"
              :handleItemClick="handleFailedMessageAction"
              :items="['View Error', 'Retry', 'Delete']"
            />
          </template>
        </data-table>
      </div>

      <!-- Message Settings Tab -->
      <div v-if="activeTab === 'message-settings'" class="message-settings-tab">
        <WhatsAppMessageSettings />
      </div>

      <!-- Cost & Billing Tab -->
      <div v-if="activeTab === 'billing'" class="billing-tab">
        <CostBilling :packageData="packageData" />
      </div>
    </div>

    <!-- Message Details Dialog -->
    <MessageDetailsDialog
      :visible="showMessageDialog"
      :messageId="selectedMessageId"
      @close="closeMessageDialog"
      @retry-success="onRetrySuccess"
    />
  </div>
</template>

<script>
/* eslint-disable */

import Button from "@/components/Button";
import DataTable from "@/components/DataTable";
import DropdownMenu from "@/components/DropdownMenu";
import Select from "@/components/Select.vue";
import Input from "@/components/Input";
import PageHeading from "@/components/PageHeading";
import MessageDetailsDialog from "./components/MessageDetailsDialog.vue";
import CostBilling from "./components/CostBilling.vue";
import WhatsAppMessageSettings from "./components/WhatsAppMessageSettings.vue";

// Import API service
import { whatsappApi } from "./services/whatsappApi";

// Import data from separate files
import { billingData } from "./data/statistics";
import { dateFilterOptions, statusFilterOptions } from "./data/filters";
import { messageColumns, failedMessageColumns } from "./data/tableColumns";

export default {
  name: "WhatsAppSettings",
  components: {
    Button,
    DataTable,
    DropdownMenu,
    Select,
    Input,
    PageHeading,
    MessageDetailsDialog,
    CostBilling,
    WhatsAppMessageSettings,
  },
  data() {
    return {
      activeTab: "messages",
      loading: false,
      searchQuery: "",
      selectedDateFilter: "today",
      selectedStatusFilter: "all",
      messages: [],
      failedMessages: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      showMessageDialog: false,
      selectedMessageId: null,
      billingData,
      dateFilterOptions,
      statusFilterOptions,
      messageColumns,
      failedMessageColumns,
      packageData: {
        package: 0,
        sent: 0,
        remaining: 0,
        subscription_date: null,
      },
      apiStats: {
        sent: 0,
        inQueue: 0,
        processing: 0,
        failed: 0,
      },
    };
  },
  computed: {
    statistics() {
      return {
        totalSentToday: this.apiStats.sent || 0,
        inQueue: this.apiStats.inQueue || 0,
        processing: this.apiStats.processing || 0,
        failed: this.apiStats.failed || 0,
      };
    },
    filteredMessages() {
      let filtered = this.messages;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (msg) =>
            msg.patient?.name?.toLowerCase().includes(query) ||
            "" ||
            msg.phone.includes(query) ||
            msg.reportType.toLowerCase().includes(query)
        );
      }

      if (this.selectedStatusFilter !== "all") {
        filtered = filtered.filter(
          (msg) => msg.status.toLowerCase() === this.selectedStatusFilter
        );
      }
      const resAPI = filtered.map((msg) => ({
        ...msg,
        snedStatus: msg.status.toUpperCase(),
      }));
      return resAPI;
    },
    failedMessagesComputed() {
      return this.messages.filter((msg) => msg.status === "failed");
    },
    failedMessagesCount() {
      return this.failedMessagesComputed.length;
    },
  },
  watch: {
    activeTab(newTab) {
      if (
        newTab === "messages" ||
        newTab === "failed" ||
        newTab === "billing"
      ) {
        this.fetchMessages();
      }
      // Message settings tab doesn't need to fetch messages
    },
  },
  methods: {
    transformMessageData(apiMessage) {
      return {
        id: apiMessage.id,
        patient: apiMessage.patient?.name || "Unknown Patient",
        phone: apiMessage.phone,
        reportType: apiMessage.reportType,
        status: apiMessage.status,
        time: new Date(
          apiMessage.sentAt || apiMessage.createdAt
        ).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        message: apiMessage.message,
        reportUrl: apiMessage.reportUrl,
        failureReason: apiMessage.failureReason,
        retryCount: apiMessage.retryCount,
        sentAt: apiMessage.sentAt,
        createdAt: apiMessage.createdAt,
        updatedAt: apiMessage.updatedAt,
        apiResponse: apiMessage.apiResponse,
        // Keep original data for calculations
        originalData: apiMessage,
      };
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    filterByDate(value) {
      this.selectedDateFilter = value ? value.value : "today";
      this.fetchMessages();
    },
    filterByStatus(value) {
      this.selectedStatusFilter = value ? value.value : "all";
    },
    async fetchMessages() {
      try {
        this.loading = true;
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
        };

        // Add status filter if not 'all'
        if (this.selectedStatusFilter !== "all") {
          params.status = this.selectedStatusFilter;
        }

        const response = await whatsappApi.getMessages(params);

        if (response.messages) {
          this.messages = response.messages.map(this.transformMessageData);
          this.pagination = {
            page: parseInt(response.page),
            limit: parseInt(response.limit),
            total: response.total,
            totalPages: response.totalPages,
          };

          // Update package data from subscription object in API response
          if (response.subscription) {
            this.packageData = {
              package: response.subscription.package,
              sent: response.subscription.sent,
              remaining: response.subscription.remaining,
              subscription_date: response.subscription.subscription_date,
            };
          }

          // Update API statistics - use total from API response for sent messages
          this.apiStats = {
            sent: response.total || 0, // Use total from API response
            inQueue: this.calculateInQueue(response.messages),
            processing: this.calculateProcessing(response.messages),
            failed: this.calculateFailed(response.messages),
          };
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        this.$toasted
          .error("Failed to fetch messages. Please try again.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },
    refreshMessages() {
      this.fetchMessages();
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchMessages();
    },
    async handleMessageAction(action, rowData) {
      console.log("Message action:", action, rowData);

      switch (action) {
        case "Retry":
          try {
            await whatsappApi.retryMessage(rowData.id);
            this.$toasted
              .success("Message retry initiated successfully")
              .goAway(3000);
            await this.fetchMessages(); // Refresh data
          } catch (error) {
            console.error("Error retrying message:", error);
            this.$toasted
              .error("Failed to retry message. Please try again.")
              .goAway(3000);
          }
          break;
        case "Delete":
          try {
            await whatsappApi.deleteMessage(rowData.id);
            this.$toasted.success("Message deleted successfully").goAway(3000);
            await this.fetchMessages(); // Refresh data
          } catch (error) {
            console.error("Error deleting message:", error);
            this.$toasted
              .error("Failed to delete message. Please try again.")
              .goAway(3000);
          }
          break;
        case "View Details":
          this.selectedMessageId = rowData.id;
          this.showMessageDialog = true;
          break;
      }
    },
    async handleFailedMessageAction(action, rowData) {
      console.log("Failed message action:", action, rowData);

      switch (action) {
        case "Retry":
          try {
            await whatsappApi.retryMessage(rowData.id);
            this.$toasted
              .success("Failed message retry initiated successfully")
              .goAway(3000);
            await this.fetchMessages(); // Refresh data
          } catch (error) {
            console.error("Error retrying failed message:", error);
            this.$toasted
              .error("Failed to retry message. Please try again.")
              .goAway(3000);
          }
          break;
        case "Delete":
          try {
            await whatsappApi.deleteMessage(rowData.id);
            this.$toasted
              .success("Failed message deleted successfully")
              .goAway(3000);
            await this.fetchMessages(); // Refresh data
          } catch (error) {
            console.error("Error deleting failed message:", error);
            this.$toasted
              .error("Failed to delete message. Please try again.")
              .goAway(3000);
          }
          break;
        case "View Error":
          // Implement view error logic
          console.log("View error for:", rowData);
          break;
      }
    },
    async retryAllFailed() {
      console.log("Retrying all failed messages");
      const failedMsgs = this.failedMessagesComputed;

      try {
        this.loading = true;
        const retryPromises = failedMsgs.map((msg) =>
          whatsappApi.retryMessage(msg.id)
        );
        await Promise.all(retryPromises);
        this.$toasted
          .success(`Retry initiated for ${failedMsgs.length} failed messages`)
          .goAway(3000);
        await this.fetchMessages(); // Refresh data
      } catch (error) {
        console.error("Error retrying all failed messages:", error);
        this.$toasted
          .error("Failed to retry some messages. Please try again.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },
    closeMessageDialog() {
      this.showMessageDialog = false;
      this.selectedMessageId = null;
    },
    onRetrySuccess() {
      this.fetchMessages(); // Refresh the messages list
    },
    calculateInQueue(messages) {
      return messages.filter(
        (msg) =>
          msg.apiResponse?.documentResponse?.data?.status === "in_progress" ||
          msg.status === "queued"
      ).length;
    },
    calculateProcessing(messages) {
      return messages.filter(
        (msg) =>
          msg.apiResponse?.documentResponse?.data?.status === "in_progress" ||
          msg.status === "processing"
      ).length;
    },
    calculateFailed(messages) {
      return messages.filter((msg) => msg.status === "failed").length;
    },
    calculateDaysRemaining(subscriptionDate) {
      if (!subscriptionDate) return 0;

      const today = new Date();
      const subDate = new Date(subscriptionDate);

      // Calculate the end of the month for the subscription date
      const endOfMonth = new Date(
        subDate.getFullYear(),
        subDate.getMonth() + 1,
        0
      );

      // Calculate days remaining
      const timeDiff = endOfMonth.getTime() - today.getTime();
      const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

      return Math.max(0, daysRemaining);
    },
  },
  mounted() {
    this.fetchMessages();
  },
};
</script>

<style scoped>
.wrapper {
  background: var(--canvas);
  min-height: 100vh;
}

.whatsapp-filters {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.whatsapp-filters .toolbar-select {
  width: 160px;
}

.toolbar-search {
  width: 240px;
  margin: 0;
}

.toolbar-action {
  min-height: 36px;
}

.tabs-container {
  padding: 10px 24px 0;
  background: var(--canvas);
  border-bottom: 1px solid var(--line);
}

.tabs-nav {
  display: flex;
  align-items: center;
  gap: 24px;
  width: fit-content;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.tab-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 42px;
  padding: 8px 0 12px;
  border-radius: 0;
  color: var(--muted);
  cursor: pointer;
  font-size: var(--fs-sm);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  transition: color var(--dur) var(--ease);
}

.tab-item:hover,
.tab-item.active {
  color: var(--accent-600);
}

.tab-item::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  border-radius: 99px 99px 0 0;
  background: transparent;
}

.tab-item.active::after {
  background: var(--accent-600);
}

.tab-icon {
  color: var(--muted-soft);
  font-size: 17px;
}

.tab-item.active .tab-icon {
  color: var(--accent-600);
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  min-height: 18px;
  padding: 1px 6px;
  border-radius: 99px;
  background: var(--danger-bg);
  color: var(--danger);
  font-size: var(--fs-eyebrow);
  font-weight: 700;
}

.tab-content {
  padding: 18px 24px 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  color: var(--accent-600);
  font-size: 18px;
}

.stat-card.sent .stat-icon {
  background: rgba(31, 122, 82, 0.1);
  color: var(--success);
}

.stat-card.queued .stat-icon {
  background: rgba(154, 98, 18, 0.1);
  color: var(--warning);
}

.stat-card.failed .stat-icon {
  background: var(--danger-bg);
  color: var(--danger);
}

.stat-content h3 {
  margin: 0 0 3px;
  color: var(--muted);
  font-size: var(--fs-sm);
  font-weight: 600;
  line-height: 1.25;
}

.stat-number {
  color: var(--ink-900);
  font-size: var(--fs-h1);
  font-weight: 700;
  line-height: 1.15;
}

.table-container {
  border: 0;
  border-radius: 0;
  background: transparent;
  overflow: visible;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1;
}

.status-badge.sent {
  background: rgba(31, 122, 82, 0.1);
  color: var(--success);
}

.status-badge.queued {
  background: rgba(154, 98, 18, 0.1);
  color: var(--warning);
}

.status-badge.processing {
  background: var(--accent-050);
  color: var(--accent-600);
}

.status-badge.failed {
  background: var(--danger-bg);
  color: var(--danger);
}

.failed-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.failed-header h3 {
  margin: 0;
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
}

.error-message {
  color: var(--danger);
  font-size: var(--fs-sm);
  font-weight: 600;
}

@media screen and (max-width: 1180px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 760px) {
  .whatsapp-filters,
  .toolbar-search,
  .whatsapp-filters .toolbar-select {
    width: 100%;
  }

  .tabs-nav {
    width: 100%;
    gap: 20px;
    overflow-x: auto;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .tab-content,
  .tabs-container {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
